/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
import { Point, IPoint } from 'pixi.js-legacy';
import { FancyInterface } from '@appCanvasCommon/interfaces/fancy-interface';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import { Camera } from '@appCanvasCommon/core/Camera';
import { lightenColor, getMostReadableColor, convertHexStringToNumber } from 'utils/color-utils';
import { LevelOfDetail } from '@appPlanApplication/lod/LevelOfDetail';
import { Ticket, TicketFields, TicketRenderTypes, TicketType } from '@touchplan/common';
import { TicketInteractionContainer } from './TicketInteractionContainer';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketPlanApplication } from '@appPlanApplication/TicketPlanApplication';
import { cssVar } from '@touchplan/css-var';
import { Easing } from '@appCanvasCommon/tween/Easing';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { setTicketWidthHelper } from 'src/app/utils/ticket-utils';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { Tween } from '@appCanvasCommon/tween/Tween';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { moveContainerToNewLayer } from 'utils/ticket-utils';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

export enum TICKET_CHANGE_EVENTS {
	CHANGED_ISACTUALIZED = 'changedIsActualized',
	CHANGED = 'changed',
}

export enum TicketLayoutMode {
	WORLD = 'world',
	STAGE = 'stage',
}

export interface ILayoutDimensionsChangedPayload {
	width: number;
	height: number;
	layoutMode: TicketLayoutMode;
}

export interface TicketLayout {
	width: number;
	height: number;
	left: number;
	top: number;
	currentLayer: LayerPaths;
}

const INITIAL_TICKET_LAYOUT: TicketLayout = {
	width: 0,
	height: 0,
	left: 0,
	top: 0,
	currentLayer: LayerPaths.TICKETS,
};

// Threshold for switching to granular view.
// A value of 1 means 100% zoom (world units match screen units, i.e., 1:1 ratio).
export const GRANULAR_VIEW_SCALE_THRESHOLD = 1;

/**
 * A DataContainer class which is used to store data of differing types within the scene graph.
 * See https://mocasystems.atlassian.net/browse/MOC-2552 for spec information.
 */
export class TicketContainer extends FancyContainer implements FancyInterface {
	/**
	 * MOC-2916. SHOULD BE FINE. SHADOW _MAY_ BE WEIRD IN SOME CIRCUMSTANCES, BUT IT IS SUBTLE. DON'T TELL ANYONE.
	 * "Okay, I'm fine with that" - Kurtis, 10/24/2018
	 */
	public BELOW: FancyContainer = new FancyContainer();
	public SURFACE: TicketInteractionContainer;
	public ABOVE: FancyContainer = new FancyContainer();
	public HIGHEST: FancyContainer = new FancyContainer();

	public override renderable = true;
	public components: ComponentManager;
	/**
	 * An object which contains all current assigned data for this data node.
	 */
	public data: any;
	/**
	 * Singleton instance of the projectMemebrs list.
	 */
	public projectMembers: any;
	public tweenManager: any;
	public stage: any;
	public camera: Camera;
	public ticketManager: any;
	public screen: any;
	public graphics: FancyGraphics;
	public liveRoleTextColor: number;
	public liveRoleColor: number;
	public application: TicketPlanApplication;
	public locationTextColor: number;
	public locationColor: number;
	public isGhost = false;

	private wtfClear = [
		'application',
		'tweenManager',
		'stage',
		'camera',
		'ticketManager',
		'screen',
		'graphics',
	];

	public locationContainer: any;
	public sprite: any;
	public canvasAngularLand: CanvasAngularLand;

	private _liveRoleLastColor = -1;
	private _locationLastColor = -1;
	private _tweener: Tween;
	private _ticketLayout: TicketLayout = INITIAL_TICKET_LAYOUT;

	constructor(canvasAngularLand: CanvasAngularLand) {
		super();
		const canvasConnectorApplication = canvasAngularLand.canvasConnector.application as TicketPlanApplication;
		this.application = canvasConnectorApplication;
		this.tweenManager = canvasConnectorApplication.tweenManager;
		this.stage = canvasConnectorApplication.stage;
		this.camera = canvasConnectorApplication.camera;
		this.ticketManager = canvasConnectorApplication.ticketManager;
		this.screen = canvasConnectorApplication.viewport;
		this.graphics = canvasConnectorApplication._graphics;
		this.canvasAngularLand = canvasAngularLand;

		this.components = new ComponentManager(this);
	}

	public setup(): void {
		this.SURFACE = new TicketInteractionContainer(this.canvasAngularLand, this);

		this.addChild(this.BELOW);
		this.addChild(this.SURFACE);
		this.addChild(this.ABOVE);
		this.addChild(this.HIGHEST);

		const data = this.getData();

		if (data.isGhost) {
			this.eventMode = EventMode.Disabled;
			this.interactiveChildren = false;
		} else {
			this.interactiveChildren = true;
			this.BELOW.interactiveChildren = false;
			this.ABOVE.interactiveChildren = true;
			this.eventMode = EventMode.Disabled;
		}
	}
	/**
	 * Get the data assigned to this DataContainer
	 * @returns DataContainer.data
	 */
	public override getData(): Ticket {
		return this.data;
	}

	/**
	 * Get the layout information to be used by canvas to render this ticket.
	 * @returns DataContainer.layout
	 */
	public getLayout(): TicketLayout {
		return this._ticketLayout;
	}

	/**
	 * Set the layout information to be used by canvas to render this ticket.
	 * @param {TicketContainer} ticketContainer
	 */
	public setLayout(layout: TicketLayout): TicketLayout {
		this._ticketLayout = layout || INITIAL_TICKET_LAYOUT;
		return this._ticketLayout;
	}

	/**
	 * Assign new data to this DataContainer
	 * @returns DataContainer
	 */
	public assign(data: any) {
		this.data = data;
		return this;
	}

	public override destroy(options?: any) {
		// Destroy textures
		this.components.destroy(options);
		this.components = null;
		this.data = null;
		this.wtfClear.forEach((wtf) => (this[wtf] = null));
		// MOC-2937 - Keep a reference to our active tween and force complete/stop it when the ticket container is destroyed
		// This will fix the edge case of trying to tween a ticket that was destroyed...and no longer has any properties to tween
		if (this._tweener) {
			this._tweener.destroy();
		}
		super.destroy(options);
	}

	private updateTicketLayout(): void {
		const { view } = this.getData();
		const { scale } = this.camera;

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7431
		// This is a temporary feature flag to disable the maximum zoom level in GTP.
		// Using sincronous flag check for convenience since this will remove soon.
		const isMaxZoomEnabled = this.canvasAngularLand.featureFlagManager.getFlag(
			DynamicFlagTypes.GTP_ENABLE_MAXIMUM_ZOOM
		);

		if (!isMaxZoomEnabled) {
			this._ticketLayout.currentLayer = LayerPaths.TICKETS;
			this._ticketLayout.width = view.width ?? 0;
			this._ticketLayout.height = view.height ?? 0;
			this._ticketLayout.left = view.left ?? 0;
			this._ticketLayout.top = view.top ?? 0;
			return;
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7431

		const isGranularView = scale.x > GRANULAR_VIEW_SCALE_THRESHOLD;
		const ticketLayer = isGranularView ? LayerPaths.UI_GRANULAR_TICKETS : LayerPaths.TICKETS;

		// If layout mode didn’t change, just update position
		if (this._ticketLayout.currentLayer !== ticketLayer) {
			// Update layout mode
			this._ticketLayout.currentLayer = ticketLayer;
			// Switch container based on mode
			const container = this.application.layerManager.getLayer(this._ticketLayout.currentLayer);
			if (container) {
				moveContainerToNewLayer(container, this);
			}
		}

		// Update dimensions and position
		const ticketType = TicketUtils.getTicketType(this.getData());
		const baseWidth = view.width ?? 0;

		this._ticketLayout.width = this.shouldKeepFixedWidth(ticketType, isGranularView)
			? baseWidth
			: isGranularView
			? this.camera.scaleToScreenX(baseWidth)
			: baseWidth;
		this._ticketLayout.height = view.height ?? 0;

		this.updatePosition(view, isGranularView);
	}

	private shouldKeepFixedWidth(ticketType: TicketType, isGranularView: boolean): boolean {
		return isGranularView && (ticketType === TicketType.Constraint || ticketType === TicketType.Milestone);
	}

	private updatePosition(view: TicketFields, isGranularView: boolean): void {
		let newLeft: number, newTop: number;
		if (isGranularView) {
			newLeft = this.camera.realToScreenX(view.left);
			newTop = this.camera.realToScreenY(view.top);
		} else {
			newLeft = view.left || 0;
			newTop = view.top || 0;
		}

		// Only update position if it changed
		if (this._ticketLayout.left !== newLeft || this._ticketLayout.top !== newTop) {
			this._ticketLayout.left = newLeft;
			this._ticketLayout.top = newTop;
			this.position.set(newLeft, newTop);
		}
	}

	public update(
		canvasAngularLand: CanvasAngularLand,
		dataContainer: TicketContainer,
		forceDirty: boolean = false
	) {
		// MOC-2953
		if (!this.visible) {
			return;
		}
		// this method *absolutely* has to be called first so the ticket layout doesn’t break spectacularly.
		// Should it come after the visibility check? Yeah, maybe… but let’s live dangerously.
		this.updateTicketLayout();
		this._cull();
		if (this.renderable) {
			this.components.update(canvasAngularLand, dataContainer, forceDirty);
		}
	}

	public updateData(canvasAngularLand: CanvasAngularLand, dataContainer: TicketContainer) {
		// this method has to be called first or the ticket layout will decide to freestyle its own design.
		this.updateTicketLayout();
		const data: Ticket = this.getData();
		const layout: TicketLayout = this.getLayout();

		// MOC-2953. We still need to trigger an update of components on data change so internal state is updated...
		this.visible = !data.hidden;
		if (this.visible) {
			this.attach();
		} else {
			this.detach();
		}

		this.eventMode = data.isFiltered ? EventMode.None : EventMode.Static;

		//Set ticket alpha
		if (typeof data.view.overwriteTransparency === 'number') {
			this.BELOW.alpha = data.view.overwriteTransparency;
			this.SURFACE.alpha = data.view.overwriteTransparency;
			this.ABOVE.alpha = data.view.overwriteTransparency;
		} else if (data.view.isActualized && !(data as any).isGhost) {
			// this.alpha = 0.5;
			this.BELOW.alpha = 0.5;
			this.SURFACE.alpha = 0.5;
			this.ABOVE.alpha = 0.5;
		} else if (!(data as any).isGhost) {
			// this.alpha = 1.0;
			this.BELOW.alpha = 1.0;
			this.SURFACE.alpha = 1.0;
			this.ABOVE.alpha = 1.0;
		} else {
			// this.alpha = 0.5;
			this.BELOW.alpha = 0.5;
			this.SURFACE.alpha = 0.5;
			this.ABOVE.alpha = 0.5;
		}

		this._createTextColor();

		// this.components.updateData(canvasAngularLand, dataContainer);

		this._animate(data, layout);

		this.emit(TICKET_CHANGE_EVENTS.CHANGED, dataContainer);
	}

	/**
	 * Animates ticket movement
	 */
	private _animate(data: Ticket, layout: TicketLayout): void {
		if (data.renderType === TicketRenderTypes.drag || (data as any).isGhost) {
			this.x = layout.left;
			this.y = layout.top;
		} else if (this.x !== layout.left || this.y !== layout.top) {
			this._tweener = this.tweenManager.tween(
				this,
				{
					x: layout.left,
					y: layout.top,
				},
				500,
				{
					easing: Easing.QUADRATIC.IN_OUT,
					onComplete: () => {
						this.x = layout.left;
						this.y = layout.top;
					},
				}
			);

			this._tweener.start();
		}
	}

	private _createTextColor(): void {
		const data: Ticket = this.getData();

		if (!data.isGhost) {
			// Get the color key or fallback to "black". MOC-2790
			['location', 'liveRole'].forEach((key: string) => {
				if (this[key + 'Color'] === this['_' + key + 'LastColor']) {
					return;
				}
				const colorString =
					data.view[key] && data.view[key].view.color ? data.view[key].view.color : cssVar.black;

				// const colorString: string = (data.view[key] && data.view[key].view.color) ? data.view[key].view.color : cssVar.black;
				const lighterColor: string = data.view.isActualized ? lightenColor(colorString, 40) : colorString;

				const mostReadableColor = getMostReadableColor(lighterColor);
				this[key + 'TextColor'] = convertHexStringToNumber(mostReadableColor);
				this['_' + key + 'LastTextColor'] = this[key + 'TextColor'];
				this[key + 'Color'] = convertHexStringToNumber(colorString);
			});
		} else {
			this.liveRoleTextColor = 0x000000;
			this.liveRoleColor = 0xffffff;
			this._liveRoleLastColor = this.liveRoleColor;

			this.locationTextColor = 0x000000;
			this.locationColor = 0xffffff;
			this._locationLastColor = this.locationColor;
		}
	}

	private _cull() {
		this.updateRenderable();
		if (this.renderable && this.isDetached) {
			this.attach();
		} else if (!this.renderable && !this.isDetached) {
			this.detach();
		}
	}

	public updateRenderable(): void {
		const data = this.getData();
		const layout = this.getLayout();
		const cameraScale: Point = this.camera.scale;
		const point: IPoint = this.toGlobal(new Point(0, 0)); // 0,0 for top-left corner of this ticket

		const width = setTicketWidthHelper(data, layout, this.canvasAngularLand.wideTicketSize$.getValue());
		this.renderable =
			LevelOfDetail.isForced ||
			!(
				point.x + width * cameraScale.x < this.screen.x ||
				point.x >= this.camera.view.width ||
				point.y + layout.height * cameraScale.y < this.screen.y ||
				point.y >= this.camera.view.height
			);
	}
}
